import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSocial } from "@/context/SocialContext";
import { useCall } from "@/context/CallContext";
import api from "@/lib/api";
import { useChatSocket } from "@/hooks/useChatSocket";
import Avatar from "@/components/chat/Avatar";
import MessageBubble from "@/components/chat/MessageBubble";
import MessageComposer from "@/components/chat/MessageComposer";
import NewChatDialog from "@/components/chat/NewChatDialog";
import MoodIndicator, { moodStyleFor } from "@/components/chat/MoodIndicator";
import SmartReplyBar from "@/components/chat/SmartReplyBar";
import AnonRoomsPanel from "@/components/chat/AnonRoomsPanel";
import MoodTimeline from "@/components/chat/MoodTimeline";
import WorldThemePanel from "@/components/world/WorldThemePanel";
import RelationshipCard from "@/components/world/RelationshipCard";
import AtmosphereLayer from "@/components/world/AtmosphereLayer";
import AIDirectorCard from "@/components/world/AIDirectorCard";
import SurpriseEventCard from "@/components/world/SurpriseEventCard";
import StoryModeModal from "@/components/world/StoryModeModal";
import CollectionsModal from "@/components/world/CollectionsModal";
import DiscoveryLabPanel from "@/components/world/DiscoveryLabPanel";
import { useDesktopNotifications } from "@/hooks/useDesktopNotifications";
import MobileActionSheet from "@/components/chat/MobileActionSheet";
import OnboardingSlider, { hasSeenOnboarding } from "@/components/onboarding/OnboardingSlider";
import AppNav from "@/components/nav/AppNav";
import { LogOut, Plus, Search, Users, MessageSquare, Circle, Ghost, TrendingUp, Sparkles, Trophy, BookOpen, ScrollText, Beaker, MoreHorizontal, Phone, Video } from "lucide-react";

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const same = d.toDateString() === now.toDateString();
  if (same) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString();
}

export default function ChatApp() {
  const { user, logout } = useAuth();
  const { setActiveConversation } = useSocial();
  const { startCall } = useCall();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(() => !hasSeenOnboarding());
  const [anonOpen, setAnonOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [worldOpen, setWorldOpen] = useState(false);
  const [relOpen, setRelOpen] = useState(false);
  const [storyOpen, setStoryOpen] = useState(false);
  const [collectionsOpen, setCollectionsOpen] = useState(false);
  const [labOpen, setLabOpen] = useState(false);
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
  const [worldByConv, setWorldByConv] = useState({});
  const [lastWsEvent, setLastWsEvent] = useState(null);
  const [atmosphereOn, setAtmosphereOn] = useState(() => {
    try { return localStorage.getItem("yappo-atmosphere") !== "0"; } catch { return true; }
  });

  // Anon room active session
  const [anonSession, setAnonSession] = useState(null); // { room, mask }
  const [anonMessages, setAnonMessages] = useState([]);

  // Mood state per conversation
  const [moodByConv, setMoodByConv] = useState({});
  const [moodLoadingByConv, setMoodLoadingByConv] = useState({});

  const scrollRef = useRef(null);
  const composerRef = useRef(null);

  const activeConv = useMemo(
    () => conversations.find((c) => c.id === activeId),
    [conversations, activeId]
  );

  const isAnon = !!anonSession;
  const currentMood = activeConv ? moodByConv[activeConv.id] : null;

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/conversations");
      setConversations(data);
    } catch {}
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  const loadMessages = useCallback(async (convId) => {
    if (!convId) return;
    try {
      const { data } = await api.get(`/conversations/${convId}/messages`);
      setMessages(data);
    } catch {}
  }, []);

  useEffect(() => {
    if (activeId) {
      loadMessages(activeId);
      api.post(`/conversations/${activeId}/read`).catch(() => {});
    } else if (!isAnon) {
      setMessages([]);
    }
  }, [activeId, loadMessages, isAnon]);

  // Tell SocialProvider which conversation is currently open so it doesn't
  // increment the unread badge for its incoming messages.
  useEffect(() => {
    setActiveConversation(activeId);
    return () => setActiveConversation(null);
  }, [activeId, setActiveConversation]);

  // Auto-fetch mood: on open + after new messages arrive (debounced)
  const moodRefreshTimer = useRef(null);
  const lastMoodMsgCount = useRef({});
  useEffect(() => {
    if (!activeConv) return;
    const convId = activeConv.id;
    const count = messages.length;
    // Initial fetch
    if (!moodByConv[convId] && count > 0) {
      refreshMood(convId);
      lastMoodMsgCount.current[convId] = count;
      return;
    }
    // Debounced auto-refresh: fetch mood 2s after new messages arrive
    // (only if we haven't refreshed for the current message count)
    const lastCount = lastMoodMsgCount.current[convId] || 0;
    if (count > lastCount) {
      clearTimeout(moodRefreshTimer.current);
      moodRefreshTimer.current = setTimeout(() => {
        refreshMood(convId);
        lastMoodMsgCount.current[convId] = count;
      }, 2500);
    }
    return () => clearTimeout(moodRefreshTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConv?.id, messages.length]);

  const refreshMood = async (convId) => {
    setMoodLoadingByConv((m) => ({ ...m, [convId]: true }));
    try {
      const { data } = await api.post("/ai/mood", { conversation_id: convId });
      setMoodByConv((m) => ({ ...m, [convId]: data }));
    } catch {} finally {
      setMoodLoadingByConv((m) => ({ ...m, [convId]: false }));
    }
  };

  // Scroll to bottom
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, activeId, anonMessages, isAnon]);

  const handleWsEvent = useCallback((evt) => {
    if (evt.type === "message") {
      const msg = evt.message;
      if (msg.anon_room_id) {
        if (anonSession && msg.anon_room_id === anonSession.room.id) {
          setAnonMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
        }
        return;
      }
      setMessages((prev) => {
        if (msg.conversation_id !== activeId) return prev;
        if (prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
      loadConversations();
      if (msg.conversation_id === activeId && msg.sender_id !== user?.id) {
        api.post(`/conversations/${msg.conversation_id}/read`).catch(() => {});
      }
      // Desktop notification when tab is hidden and message not from me
      if (msg.sender_id !== user?.id && document.hidden) {
        const preview = msg.content_type === "doodle" ? "🎨 Çizim gönderdi" : msg.content_type === "voice" ? "🎤 Sesli not gönderdi" : msg.content;
        notify(`${msg.sender_name}`, preview?.slice(0, 120) || "Yeni mesaj");
      }
    } else if (evt.type === "reaction") {
      const { message_id, reactions } = evt;
      setMessages((prev) => prev.map((m) => (m.id === message_id ? { ...m, reactions } : m)));
      setAnonMessages((prev) => prev.map((m) => (m.id === message_id ? { ...m, reactions } : m)));
    } else if (evt.type === "world") {
      const { conversation_id, theme } = evt;
      setWorldByConv((w) => ({ ...w, [conversation_id]: { ...(w[conversation_id] || {}), theme } }));
    } else if (evt.type === "surprise_completed") {
      setLastWsEvent(evt);
    } else if (evt.type === "typing") {
      const { conversation_id, user_id, user_name, is_typing } = evt;
      if (user_id === user?.id) return;
      setTypingUsers((prev) => {
        const forConv = { ...(prev[conversation_id] || {}) };
        if (is_typing) forConv[user_id] = { name: user_name, ts: Date.now() };
        else delete forConv[user_id];
        return { ...prev, [conversation_id]: forConv };
      });
    } else if (evt.type === "read") {
      const { conversation_id, user_id } = evt;
      if (conversation_id === activeId && user_id !== user?.id) {
        setMessages((prev) =>
          prev.map((m) =>
            (m.read_by || []).includes(user_id) ? m : { ...m, read_by: [...(m.read_by || []), user_id] }
          )
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, anonSession, loadConversations, user?.id]);

  const { connected, send } = useChatSocket({ enabled: !!user, onMessage: handleWsEvent });
  const { notify } = useDesktopNotifications({ user, enabled: !!user });

  // Refetch messages + conversations whenever the WS reconnects so anything
  // that was sent while we were disconnected isn't invisible until the next
  // manual reload. Depends only on `connected` transitioning true.
  const prevConnectedRef = useRef(false);
  useEffect(() => {
    if (connected && !prevConnectedRef.current) {
      loadConversations();
      if (activeId) loadMessages(activeId);
    }
    prevConnectedRef.current = connected;
  }, [connected, activeId, loadConversations, loadMessages]);

  // Rejoin anon room on WS reconnect
  useEffect(() => {
    if (connected && anonSession) {
      send({ type: "join_anon", room_id: anonSession.room.id });
    }
  }, [connected, anonSession, send]);

  // Prune stale typing
  useEffect(() => {
    const iv = setInterval(() => {
      setTypingUsers((prev) => {
        const now = Date.now();
        const next = {};
        for (const [cid, users] of Object.entries(prev)) {
          const filtered = Object.fromEntries(Object.entries(users).filter(([, v]) => now - v.ts < 3000));
          if (Object.keys(filtered).length) next[cid] = filtered;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, []);

  // Load world theme when conversation changes
  useEffect(() => {
    if (!activeConv) return;
    if (worldByConv[activeConv.id]) return;
    (async () => {
      try {
        const { data } = await api.get(`/conversations/${activeConv.id}/world`);
        setWorldByConv((w) => ({ ...w, [activeConv.id]: { theme: data.theme, config: data.config } }));
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConv?.id, worldByConv]);

  const toggleAtmosphere = () => {
    setAtmosphereOn((v) => {
      const next = !v;
      try { localStorage.setItem("yappo-atmosphere", next ? "1" : "0"); } catch {}
      return next;
    });
  };

  const handleSend = async (payload) => {
    if (isAnon) {
      try {
        const { data: sent } = await api.post("/messages", { ...payload, anon_room_id: anonSession.room.id, conversation_id: "" });
        if (sent?.id) {
          setAnonMessages((prev) => (prev.some((m) => m.id === sent.id) ? prev : [...prev, sent]));
        }
      } catch {}
      return;
    }
    if (!activeId) return;
    try {
      const isYappo = (payload.content || "").toLowerCase().includes("@yappo");
      if (isYappo) {
        // Route through explicit endpoint so AI reply is queued
        const q = payload.content.replace(/@yappo/gi, "").trim() || "Selam!";
        await api.post("/ai/ask-yappo", { conversation_id: activeId, question: q });
      } else {
        // Optimistically append the message so the sender sees it instantly
        // even if the WebSocket echo is slow. WS handler dedupes by id.
        const { data: sent } = await api.post("/messages", { ...payload, conversation_id: activeId });
        if (sent?.id) {
          setMessages((prev) => {
            if (prev.some((m) => m.id === sent.id)) return prev;
            if (sent.conversation_id && sent.conversation_id !== activeId) return prev;
            return [...prev, sent];
          });
        }
      }
      loadConversations();
    } catch {}
  };

  const handleTyping = (is_typing) => {
    if (isAnon) return;
    if (!activeId) return;
    send({ type: "typing", conversation_id: activeId, is_typing });
  };

  const filteredConversations = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      c.participants?.some((p) => p.name?.toLowerCase().includes(q) || p.email?.toLowerCase().includes(q))
    );
  }, [conversations, search]);

  const activeTypingUsers = Object.values(typingUsers[activeId] || {});
  const totalOthers = activeConv ? Math.max((activeConv.participants?.length || 1) - 1, 0) : 0;

  const openAnonRoom = async (room, mask) => {
    setAnonSession({ room, mask });
    setActiveId(null);
    try {
      const { data } = await api.get(`/anon-rooms/${room.id}/messages`);
      setAnonMessages(data);
    } catch { setAnonMessages([]); }
    send({ type: "join_anon", room_id: room.id });
  };

  const leaveAnonRoom = () => {
    if (!anonSession) return;
    send({ type: "leave_anon", room_id: anonSession.room.id });
    setAnonSession(null);
    setAnonMessages([]);
  };

  const lastMessage = messages[messages.length - 1];
  const showSmartReplies = activeConv && lastMessage && lastMessage.sender_id !== user?.id && !isAnon;

  const moodStyle = currentMood ? moodStyleFor(currentMood.mood) : null;
  const currentWorld = activeConv ? worldByConv[activeConv.id] : null;
  const worldBg = currentWorld?.config?.bg;
  const worldAccent = currentWorld?.config?.accent;
  const currentTheme = currentWorld?.theme || "meadow";
  const injectDirectorSuggestion = (s) => {
    // send as a system-styled text message
    handleSend({ content: `${s.emoji} ${s.title} — ${s.desc}`, content_type: "text" });
  };

  return (
    <div className="h-[100dvh] w-full flex overflow-hidden bg-[#F9F8F6] text-[#0A0A0A] md:pl-[72px]" data-testid="chat-app">
      {/* Bottom-nav is hidden on mobile when a conversation is open so the
          chat can take the full screen. Desktop rail stays visible via CSS. */}
      <AppNav hideMobileWhen={!!(activeId || isAnon)} />
      <aside className={`w-full md:w-[380px] h-full bg-white border-r-[3px] border-[#0A0A0A] flex flex-col pb-20 md:pb-0 ${(activeId || isAnon) ? "hidden md:flex" : "flex"}`}>
        <div className="p-4 border-b-[3px] border-[#0A0A0A] flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#CCFF00] border-hard shadow-hard-sm flex items-center justify-center">
            <MessageSquare strokeWidth={3} className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-display text-xl leading-none">yappo.</div>
            <div className="text-[11px] font-bold text-[#555] flex items-center gap-1 mt-0.5">
              <Circle strokeWidth={4} className={`w-2 h-2 ${connected ? "fill-[#CCFF00] text-[#CCFF00]" : "fill-[#FF9E80] text-[#FF9E80]"}`} />
              {connected ? "Bağlı" : "Bağlanıyor…"}
            </div>
          </div>
          <button data-testid="logout-btn" onClick={logout} aria-label="Çıkış yap"
            className="w-10 h-10 border-hard rounded-xl flex items-center justify-center bg-white press-btn shadow-hard-sm">
            <LogOut strokeWidth={2.5} className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 border-b-[3px] border-[#0A0A0A] flex items-center gap-3 bg-[#F9F8F6]">
          <Avatar name={user?.name} picture={user?.picture} size={40} online showStatus />
          <div className="flex-1 min-w-0">
            <div className="font-black truncate">{user?.name}</div>
            <div className="text-xs font-medium text-[#555] truncate">{user?.email}</div>
          </div>
          <button data-testid="open-new-chat-btn" onClick={() => setDialogOpen(true)}
            className="bg-[#D4B3FF] border-hard rounded-xl w-10 h-10 flex items-center justify-center press-btn shadow-hard-sm" aria-label="Yeni sohbet">
            <Plus strokeWidth={3} className="w-5 h-5" />
          </button>
        </div>

        {/* Anon rooms shortcut */}
        <button
          onClick={() => setAnonOpen(true)}
          data-testid="open-anon-rooms-btn"
          className="mx-3 mt-3 flex items-center gap-3 border-hard-2 rounded-xl p-3 hover:bg-[#F9F8F6] text-left press-btn shadow-hard-sm"
        >
          <div className="w-10 h-10 rounded-xl bg-[#0A0A0A] text-white border-hard-2 flex items-center justify-center">
            <Ghost strokeWidth={3} className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-black text-sm">Anonim odalar</div>
            <div className="text-[11px] text-[#555]">Maske takıp katıl</div>
          </div>
        </button>

        {/* Keşif Lab shortcut */}
        <button
          onClick={() => setLabOpen(true)}
          data-testid="open-lab-btn"
          className="mx-3 mt-2 flex items-center gap-3 border-hard-2 rounded-xl p-3 hover:bg-[#F9F8F6] text-left press-btn shadow-hard-sm"
        >
          <div className="w-10 h-10 rounded-xl bg-[#D4B3FF] border-hard-2 flex items-center justify-center">
            <Beaker strokeWidth={3} className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-black text-sm">Keşif Laboratuvarı</div>
            <div className="text-[11px] text-[#555]">Beta özelliklere oy ver</div>
          </div>
        </button>

        <div className="p-3">
          <div className="flex items-center gap-2 bg-[#F9F8F6] border-hard rounded-xl px-3 py-2">
            <Search strokeWidth={2.5} className="w-4 h-4" />
            <input data-testid="conversation-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Sohbetlerde ara…" className="flex-1 bg-transparent focus:outline-none text-sm" />
          </div>
        </div>

        <div className="flex-1 overflow-auto" data-testid="conversation-list">
          {filteredConversations.length === 0 && (
            <div className="text-center p-8 text-[#555]">
              <div className="font-display text-xl mb-2">Henüz sohbet yok</div>
              <p className="text-sm">Sağ üstteki <b>+</b> ile yeni bir sohbet başlat.</p>
            </div>
          )}
          {filteredConversations.map((c) => {
            const last = c.last_message;
            const active = c.id === activeId && !isAnon;
            return (
              <button
                key={c.id}
                data-testid={`conversation-item-${c.id}`}
                onClick={() => { leaveAnonRoom(); setActiveId(c.id); }}
                className={`w-full flex items-center gap-3 p-4 border-b-[2px] border-[#0A0A0A] text-left transition-colors ${active ? "bg-[#CCFF00]/40" : "hover:bg-[#F0F0F0]"}`}
              >
                <div className="relative">
                  {c.is_group ? (
                    <div className="w-12 h-12 rounded-full border-[2.5px] border-[#0A0A0A] bg-[#D4B3FF] flex items-center justify-center">
                      <Users strokeWidth={3} className="w-5 h-5" />
                    </div>
                  ) : (
                    <Avatar name={c.name} picture={c.picture} size={48}
                      online={c.participants?.find((p) => p.id !== user?.id)?.online} showStatus />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-black truncate">{c.name || "(isimsiz)"}</div>
                    <div className="text-[11px] font-bold text-[#555] shrink-0">{formatWhen(c.updated_at)}</div>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <div className="text-sm text-[#555] truncate">
                      {last ? (
                        <>
                          {c.is_group && last.sender_id !== user?.id && (<span className="font-bold">{last.sender_name}: </span>)}
                          {last.content_type === "doodle" ? "🎨 Çizim" : last.content_type === "voice" ? "🎤 Sesli not" : last.content}
                        </>
                      ) : <span className="italic">Henüz mesaj yok</span>}
                    </div>
                    {c.unread_count > 0 && (
                      <span data-testid={`unread-badge-${c.id}`} className="bg-[#FF3B30] text-white text-[11px] font-black px-2 py-0.5 rounded-full border-[2px] border-[#0A0A0A] shadow-hard-sm">
                        {c.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      {/* Main area */}
      <main
        className={`flex-1 h-full flex flex-col dot-grid relative overflow-hidden min-w-0 transition-[background] duration-700 ease-out pb-20 md:pb-0 ${(activeId || isAnon) ? "flex" : "hidden md:flex"}`}
        data-testid="chat-main"
        style={
          isAnon
            ? {}
            : worldBg
              ? { background: `linear-gradient(180deg, ${worldBg}, ${worldBg}CC)` }
              : (moodStyle ? { background: moodStyle.bg + ", url() " } : {})
        }>
        {!activeConv && !isAnon && (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="max-w-md text-center">
              <div className="inline-block bg-[#CCFF00] border-hard shadow-hard-lg rounded-2xl p-6 mb-4">
                <MessageSquare strokeWidth={3} className="w-12 h-12" />
              </div>
              <h2 className="font-display text-4xl mb-2">Bir sohbet seç</h2>
              <p className="text-[#555] font-medium">
                Sol taraftan bir sohbet aç, <b>+</b> ile yeni konuşma başlat ya da <b>Anonim odalar</b>a maske takıp katıl. <br />
                İpucu: mesaj içinde <span className="font-bold">@yappo</span> yazınca AI cevaplar.
              </p>
            </div>
          </div>
        )}

        {isAnon && (
          <>
            <header className="p-3 md:px-6 md:py-4 border-b-[3px] border-[#0A0A0A] bg-white flex items-center gap-2 md:gap-3 min-w-0">
              <div className="w-10 h-10 md:w-12 md:h-12 shrink-0 rounded-full border-hard-2 bg-[#0A0A0A] text-white flex items-center justify-center">
                <Ghost strokeWidth={3} className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg md:text-xl leading-none truncate" data-testid="anon-room-name">{anonSession.room.name}</div>
                <div className="text-[11px] md:text-xs font-bold text-[#555] mt-1 truncate">Sen: <b data-testid="anon-mask-name">{anonSession.mask.mask_name}</b></div>
              </div>
              <button onClick={leaveAnonRoom} data-testid="anon-leave-btn" className="shrink-0 bg-white border-hard rounded-xl px-3 py-2 text-xs font-black uppercase press-btn shadow-hard-sm">Çık</button>
            </header>
            <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto px-4 md:px-8 py-6" data-testid="messages-scroll">
              {anonMessages.length === 0 && <div className="text-center text-[#555] mt-16 font-semibold">Odaya ilk mesajı sen at 👻</div>}
              {anonMessages.map((m) => (
                <MessageBubble key={m.id} message={m} isMe={m.sender_name === anonSession.mask.mask_name} totalOthers={0} isGroup senderName={m.sender_name} hideMeta />
              ))}
            </div>
            <MessageComposer onSend={handleSend} onTyping={() => {}} />
          </>
        )}

        {activeConv && !isAnon && (
          <>
            <header className="p-3 md:px-6 md:py-4 border-b-[3px] border-[#0A0A0A] bg-white flex items-center gap-2 md:gap-4 min-w-0">
              <button className="md:hidden w-9 h-9 shrink-0 border-hard rounded-xl flex items-center justify-center press-btn shadow-hard-sm"
                onClick={() => setActiveId(null)} data-testid="back-to-list-btn" aria-label="Geri">←</button>
              {activeConv.is_group ? (
                <div className="w-10 h-10 md:w-12 md:h-12 shrink-0 rounded-full border-[2.5px] border-[#0A0A0A] bg-[#D4B3FF] flex items-center justify-center">
                  <Users strokeWidth={3} className="w-5 h-5" />
                </div>
              ) : (
                <div className="shrink-0">
                  <Avatar name={activeConv.name} picture={activeConv.picture} size={40}
                    online={activeConv.participants?.find((p) => p.id !== user?.id)?.online} showStatus />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg md:text-xl leading-none truncate">{activeConv.name}</div>
                <div className="text-[11px] md:text-xs font-bold text-[#555] mt-1 truncate">
                  {activeConv.is_group
                    ? `${activeConv.participants?.length || 0} üye`
                    : activeConv.participants?.find((p) => p.id !== user?.id)?.online ? "Çevrimiçi" : "Çevrimdışı"}
                </div>
              </div>
              <MoodIndicator
                mood={currentMood?.mood}
                emoji={currentMood?.emoji}
                reason={currentMood?.reason}
                onRefresh={() => refreshMood(activeConv.id)}
                loading={!!moodLoadingByConv[activeConv.id]}
              />
              {/* 1:1 call buttons — hidden for group chats */}
              {!activeConv.is_group && (() => {
                const other = activeConv.participants?.find((p) => p.id !== user?.id);
                if (!other) return null;
                return (
                  <>
                    <button
                      onClick={() => startCall({ toUser: { id: other.id, name: other.name, picture: other.picture }, type: "audio" })}
                      data-testid="call-voice-btn"
                      className="w-9 h-9 border-hard-2 rounded-lg flex items-center justify-center press-btn shadow-hard-sm bg-white shrink-0"
                      aria-label="Sesli ara"
                      title="Sesli ara"
                    >
                      <Phone strokeWidth={3} className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => startCall({ toUser: { id: other.id, name: other.name, picture: other.picture }, type: "video" })}
                      data-testid="call-video-btn"
                      className="w-9 h-9 border-hard-2 rounded-lg flex items-center justify-center press-btn shadow-hard-sm bg-white shrink-0"
                      aria-label="Görüntülü ara"
                      title="Görüntülü ara"
                    >
                      <Video strokeWidth={3} className="w-4 h-4" />
                    </button>
                  </>
                );
              })()}
              {/* Mobile: single MORE button opens action sheet */}
              <button
                onClick={() => setMobileActionsOpen(true)}
                data-testid="mobile-more-btn"
                className="w-9 h-9 border-hard-2 rounded-lg flex items-center justify-center press-btn shadow-hard-sm bg-white shrink-0"
                aria-label="Aksiyonlar"
                title="Daha fazla"
              >
                <MoreHorizontal strokeWidth={3} className="w-4 h-4" />
              </button>
            </header>

            <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto px-4 md:px-8 py-6 relative" data-testid="messages-scroll">
              {activeConv && !isAnon && (
                <AtmosphereLayer theme={currentTheme} mood={currentMood?.mood} enabled={atmosphereOn} />
              )}
              {messages.length === 0 && (<div className="text-center text-[#555] mt-16 font-semibold">İlk selamı sen ver 👋</div>)}
              {messages.map((m) => (
                <MessageBubble key={m.id} message={m}
                  isMe={m.sender_id === user?.id}
                  totalOthers={totalOthers} isGroup={activeConv.is_group}
                  senderName={m.sender_name}
                  myId={user?.id}
                  onReactionChange={(mid, reactions) => setMessages((prev) => prev.map((x) => (x.id === mid ? { ...x, reactions } : x)))}
                />
              ))}
              {activeTypingUsers.length > 0 && (
                <div className="flex items-center gap-2 mt-1 slide-up" data-testid="typing-indicator">
                  <div className="bg-white border-[2px] border-[#0A0A0A] shadow-hard-sm rounded-2xl rounded-tl-sm px-3 py-2 flex items-center">
                    <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
                  </div>
                  <span className="text-xs font-bold text-[#555]">{activeTypingUsers.map((u) => u.name).join(", ")} yazıyor…</span>
                </div>
              )}
            </div>

            {activeConv && (
              <SurpriseEventCard conversationId={activeConv.id} wsEvent={lastWsEvent} />
            )}

            {showSmartReplies && (
              <SmartReplyBar
                conversationId={activeConv.id}
                lastMessageId={lastMessage?.id}
                onPick={(t) => {
                  // Immediately send as regular text
                  handleSend({ content: t, content_type: "text" });
                }}
              />
            )}

            {activeConv && <AIDirectorCard conversationId={activeConv.id} onInject={injectDirectorSuggestion} />}

            <div ref={composerRef}>
              <MessageComposer onSend={handleSend} onTyping={handleTyping} />
            </div>
          </>
        )}
      </main>

      <NewChatDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={(conv) => {
          setConversations((prev) => (prev.find((c) => c.id === conv.id) ? prev : [conv, ...prev]));
          setActiveId(conv.id);
        }}
      />

      <AnonRoomsPanel
        open={anonOpen}
        onClose={() => setAnonOpen(false)}
        onJoined={openAnonRoom}
      />

      <MoodTimeline
        open={timelineOpen}
        conversationId={activeConv?.id}
        conversationName={activeConv?.name || ""}
        onClose={() => setTimelineOpen(false)}
      />

      <WorldThemePanel
        open={worldOpen}
        conversationId={activeConv?.id}
        onClose={() => setWorldOpen(false)}
        onChanged={(theme, config) => {
          setWorldByConv((w) => ({ ...w, [activeConv.id]: { theme, config } }));
        }}
      />

      <RelationshipCard
        open={relOpen}
        conversationId={activeConv?.id}
        conversationName={activeConv?.name || ""}
        onClose={() => setRelOpen(false)}
      />

      <StoryModeModal
        open={storyOpen}
        conversationId={activeConv?.id}
        conversationName={activeConv?.name || ""}
        onClose={() => setStoryOpen(false)}
      />

      <CollectionsModal
        open={collectionsOpen}
        conversationId={activeConv?.id}
        conversationName={activeConv?.name || ""}
        onClose={() => setCollectionsOpen(false)}
      />

      <DiscoveryLabPanel open={labOpen} onClose={() => setLabOpen(false)} />

      <MobileActionSheet
        open={mobileActionsOpen}
        onClose={() => setMobileActionsOpen(false)}
        atmosphereOn={atmosphereOn}
        onOpenWorld={() => setWorldOpen(true)}
        onOpenRelationship={() => setRelOpen(true)}
        onOpenStory={() => setStoryOpen(true)}
        onOpenCollections={() => setCollectionsOpen(true)}
        onOpenTimeline={() => setTimelineOpen(true)}
        onToggleAtmosphere={toggleAtmosphere}
      />

      <OnboardingSlider open={showOnboarding} onClose={() => setShowOnboarding(false)} />
    </div>
  );
}
